import { i18n } from '@lingui/core';
import { msg } from '@lingui/core/macro';
import type { SDKError } from '@saas/api-client';

const PERMISSION_DENIED_MESSAGE = msg`You don't have permission to do this. Ask a company admin to update your role.`;

export const PERMISSION_DENIED_CODE = 'PERMISSION_DENIED';

/** The create-company sub-domain already names a workspace; the form has to go back to that field. */
export const SUBDOMAIN_TAKEN_CODE = 'SUBDOMAIN_TAKEN';

// Server refusals whose copy belongs to the reader, not the API: the backend has no request locale
// on the public signup route, so the message is written here where the language is known.
const LOCALIZED_MESSAGE_BY_CODE = new Map([
	[PERMISSION_DENIED_CODE, PERMISSION_DENIED_MESSAGE],
	['EMAIL_ALREADY_REGISTERED', msg`An account with this email already exists. Log in instead, or reset your password.`],
	[SUBDOMAIN_TAKEN_CODE, msg`That Vadio address is already taken. Choose a different one.`],
]);

interface ApiErrorFields {
	status: number;
	code: string | null;
	message: string;
	/** The refusal body verbatim, so a caller can read the extra fields a code carries. */
	body?: unknown;
}

export class ApiError extends Error {
	readonly status: number;
	readonly code: string | null;
	/**
	 * The refusal body as the server sent it. Codes that carry more than a message
	 * (`RATE_LIMITED`'s `retryAfterSeconds`, `CODE_WRONG`'s `attemptsLeft`) are read off
	 * this rather than re-parsed at every call site.
	 */
	readonly body: unknown;

	constructor({ status, code, message, body }: ApiErrorFields) {
		super(message);
		this.name = 'ApiError';
		this.status = status;
		this.code = code;
		this.body = body ?? null;
	}
}

/** Reads one number off a refusal body, or null when it is absent or the wrong type. */
export const readRefusalNumber = ({ error, key }: { error: unknown; key: string }): number | null => {
	if (!(error instanceof ApiError)) return null;
	const body = error.body;
	if (body == null || typeof body !== 'object') return null;
	const value = (body as Record<string, unknown>)[key];
	return typeof value === 'number' && Number.isFinite(value) ? value : null;
};

const extractCode = (body: unknown): string | null => {
	if (body == null || typeof body !== 'object') return null;
	const code = (body as Record<string, unknown>).code;
	return typeof code === 'string' ? code : null;
};

export const isPermissionDeniedError = (error: unknown): boolean =>
	error instanceof ApiError && error.code === PERMISSION_DENIED_CODE;

export const isSubdomainTakenError = (error: unknown): error is ApiError =>
	error instanceof ApiError && error.code === SUBDOMAIN_TAKEN_CODE;

/** The pre-flight metering refusal: the cycle's AI token allowance is spent (ADR-0049). */
export const AI_QUOTA_EXHAUSTED_CODE = 'AI_QUOTA_EXHAUSTED';

export const isQuotaExhaustedError = (error: unknown): boolean =>
	error instanceof ApiError && error.code === AI_QUOTA_EXHAUSTED_CODE;

interface ToApiErrorInput {
	error: SDKError;
	fallbackMessage: string;
}

const MAX_PRESENTABLE_LENGTH = 200;

const rawMessageOf = (error: unknown): string => {
	if (error instanceof Error) return error.message;
	if (typeof error === 'string') return error;
	return '';
};

// A message fit to put in front of a user. A 4xx refusal is written for a person; a server fault or a
// dead connection carries whatever the exception threw, which is a stack trace or a driver dump.
export const presentableErrorMessage = (error: unknown): string | null => {
	if (error instanceof ApiError && (error.status < 400 || error.status >= 500)) return null;

	const message = rawMessageOf(error).trim();
	if (message.length === 0 || message.length > MAX_PRESENTABLE_LENGTH || message.includes('\n')) return null;
	return message;
};

export const toApiError = ({ error, fallbackMessage }: ToApiErrorInput): ApiError => {
	const code = extractCode(error.body);
	const localized = code === null ? undefined : LOCALIZED_MESSAGE_BY_CODE.get(code);
	const message = localized ? i18n._(localized) : error.message || fallbackMessage;
	return new ApiError({ status: error.status, code, message, body: error.body });
};
